import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { cn } from "@lib/cn.ts";
import { getVersionEntry, manifest } from "@lib/content.ts";

const STATUS_TONE: Record<string, string> = {
  stable: "text-success",
  beta: "text-info",
  deprecated: "text-warning",
  sunset: "text-danger",
};

interface Props {
  version: string;
}

export function VersionSwitcher({ version }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = getVersionEntry(version);

  const switchTo = (nextVersion: string): void => {
    // Keep the reader where they are: swap only the version segment, so
    // /v1/reference/authLogin becomes /v2/reference/authLogin.
    const rest = location.pathname.replace(/^\/[^/]+/, "");
    void navigate(`/${nextVersion}${rest}`);
  };

  if (manifest.versions.length === 1) {
    return (
      <span className="rounded-sm2 border border-line px-2 py-1 font-mono text-[12px] text-muted">
        {current?.label ?? version}
      </span>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          "inline-flex items-center gap-1 rounded-sm2 border border-line px-2 py-1",
          "font-mono text-[12px] text-ink transition hover:bg-surface-2",
        )}
      >
        {current?.label ?? version}
        <ChevronDown className="size-3 text-subtle" aria-hidden />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-52 rounded-default border border-line bg-bg p-1 shadow-lg"
        >
          {manifest.versions.map((entry) => (
            <DropdownMenu.Item
              key={entry.id}
              onSelect={() => switchTo(entry.id)}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-sm2 px-2 py-1.5 text-[13px]",
                "outline-none data-[highlighted]:bg-surface-2",
              )}
            >
              <span className="w-3.5">
                {entry.id === version ? (
                  <Check className="size-3.5 text-accent" aria-hidden />
                ) : null}
              </span>
              <span className="font-mono">{entry.label}</span>
              <span
                className={cn(
                  "ml-auto text-[11px] uppercase tracking-wide",
                  STATUS_TONE[entry.status] ?? "text-muted",
                )}
              >
                {entry.status}
              </span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
